import { EvmChainTag, getChain } from "@umi/shared/chains";
import { getCrossPlatformInfo } from "@umi/shared/crossplatform";
import { ofetch } from "ofetch";
import Queue from "p-queue";
import { custom, RpcRequestError } from "viem";
import { EthMethod } from "./methods";



// Store queues per method per chain, but initialize them lazily
const queueCache: Record<string, Record<string, Queue<any, any>>> = {};

/**
 * Get or create a queue for a specific chain and method.
 * This is done to avoid circular dependencies issues
 * with @see EVM_CHAIN_TAGS
 */
function getQueue(chainTag: EvmChainTag, method: EthMethod): Queue<any, any> {
  // Initialize chain entry if needed
  if (!queueCache[chainTag]) {
    queueCache[chainTag] = {};
  }

  // Initialize method queue if needed
  if (!queueCache[chainTag][method]) {
    const chain = getChain(chainTag);
    const { limit, windowSize } = chain
      .provider("public")
      .rateLimits
      .getMethodRateLimit(method);

    queueCache[chainTag][method] = new Queue({
      intervalCap: limit,
      interval: windowSize,
      carryoverConcurrencyCount: true,
    });
  }

  return queueCache[chainTag][method];
}


/**
 * Queue based HTTP transport for EVM chains for rate limit
 * management and batching of requests.
 * @param chainTag
 * @returns
 */
export const queuedHttp = (chainTag: EvmChainTag, rpcUrl: string) => {
  const { stage } = getCrossPlatformInfo();
  return custom({
    async request({ method, params }: { method: EthMethod; params?: any[] }) {

      const queue = getQueue(chainTag, method);

      const response = await queue.add(() =>
        ofetch.raw(rpcUrl, {
          method: "POST",
          ignoreResponseError: true,
          body: { jsonrpc: "2.0", method, params: params || [], id: Date.now() },
          onRequest() {
            if (stage === "local") console.debug(`[queuedHttp] ${chainTag} ${method}`, params);
          },
        }),
      { throwOnTimeout: true }
      );

      if (!response.ok) {
        if (stage === "local") {
          console.error(`[queuedHttp] ${chainTag} ${method} ${JSON.stringify(params)} failed`, response.status, response.statusText);
        }
        throw new Error(`RPC Error: ${response.status} ${response.statusText}`);
      }
      const data = response._data as any;
      if (data.error) throw new RpcRequestError(data.error);
      return data.result;
    },
  });
};
